import { PolyLine, Line, Text, Arc, Circle } from "./utils";
import { KABLO_BACASI_DRAWING } from "./kabloBacasiDrawingData";

const op_gray = {
  color: 0xcccccc,
  colorIndex: 8,
  layer: '0',
  shape: true,
  hasContinuousLinetypePattern: false
};
const op_blue = {
  color: 0x4287f5,
  colorIndex: 5,
  layer: '0',
  shape: true,
  hasContinuousLinetypePattern: false
};

const Scl = 1;
const rowHeight = 18 * Scl;
const textH = 10 * Scl;
const smallTextH = 8 * Scl;

function generateStableId(element) {
  const f = element.handFak || "0";
  return `text-${element.text}-${element.x}-${element.y}-${element.textHeight}-${f}`;
}

function addText(result, x, y, content, height, opts) {
  const t = { text: String(content), x, y, textHeight: height || textH };
  t.handle = generateStableId(t);
  result.push(Text(t.x, t.y, t, 0, opts || op_gray));
}

function addTextRotated(result, x, y, content, rotation, height, opts) {
  const t = { text: String(content), x, y, textHeight: height || textH };
  t.handle = generateStableId(t);
  result.push(Text(t.x, t.y, t, rotation, opts || op_gray));
}

/**
 * Kablo Bacası Hesabı Dokümanı
 * @param {Object} data - { A, K, Rt, Rtv, Rk, TIP, d, L }
 * @returns {{ result: Array, width: number, height: number }}
 */
export const createKabloBacasi = (data) => {
  const {
    A = 1,      // Kat başı bağımsız bölüm (daire) sayısı
    K = 5,      // Toplam kat sayısı
    Rt = 0.5,   // Tesisat kablo çapı (cm)
    Rtv = 0.6,  // TV/Telefon kablo çapı (cm)
    Rk = 1.8,   // Kolon hattı kablo çapı (cm)
    TIP = 4,    // Kablo merdiveni tipi (1-4)
    d = 0.15,   // Dikme profil kalınlığı (cm)
    L = 150,    // Dikme mesnet açıklığı (cm)
  } = data || {};

  const result = [];

  // ──── Hesaplamalar ────
  // Rk_top: Rk nin üst sınırı (en büyük kablo çapı)
  const Rktop = Math.max(Rt, Rtv, Rk);
  const Rkmax = Rktop; // en büyük kablo yarıçapı

  // TIP'e göre kablo merdiveni eni (a) hesabı
  let a;
  let formulText;
  switch (TIP) {
    case 1:
      a = 1.1 * A * K * (Rt + Rtv) + 3 * Rktop + 10;
      formulText = `a = 1.1 × ${A} × ${K} × (${Rt} + ${Rtv}) + 3 × ${Rktop} + 10`;
      break;
    case 2:
      a = 1.1 * A * K * (Rt + Rtv) + 2 * Rktop + 10;
      formulText = `a = 1.1 × ${A} × ${K} × (${Rt} + ${Rtv}) + 2 × ${Rktop} + 10`;
      break;
    case 3:
      a = 1.1 * A * K * (Rt + Rtv) + Rktop + 10;
      formulText = `a = 1.1 × ${A} × ${K} × (${Rt} + ${Rtv}) + ${Rktop} + 10`;
      break;
    case 4:
    default:
      a = 1.1 * A * K * (Rt + Rtv + Rk) + 10;
      formulText = `a = 1.1 × ${A} × ${K} × (${Rt} + ${Rtv} + ${Rk}) + 10`;
      break;
  }
  a = Math.round(a * 100) / 100;

  // Dikme yüksekliği
  const h_min = Math.max(Rkmax + 1, 4); // min 4cm
  const h = h_min;

  // Kablo bacası boyutları
  const depth = 20; // min 20cm
  const kbEni = Math.max(a + 10, 50); // min 50cm

  // Mukavemet kontrolü
  const E = 2100000; // kg/cm² (çelik elastisite modülü)
  // P: 1 metre kablo merdiveni yükü (basitleştirilmiş hesap)
  const P = A * K * (Rt + Rtv + Rk) * 0.5; // kg (tahmini kablo yükü)
  const I_val = (a * d * Math.pow(h - d, 3)) / 12; // moment of inertia
  const f_val = (P * Math.pow(L, 3)) / (48 * E * I_val); // sehim
  const f_limit = L / 200; // izin verilen sehim

  // ──── Sol taraf: Hesap metni ────
  const leftWidth = 500 * Scl;
  const gap = 10 * Scl;
  let Y = 0;

  // Başlık
  const titleTextH = 18 * Scl;
  addText(result, gap, Y, "KABLO BACASI HESABI", titleTextH, op_blue);
  Y -= rowHeight * 1.5;

  // Ayırıcı çizgi
  result.push(Line(0, Y, leftWidth, Y, op_gray));
  Y -= rowHeight;

  // Parametre başlığı
  addText(result, gap, Y, "HESAP PARAMETRELERİ", 12 * Scl, op_blue);
  Y -= rowHeight * 1.2;

  // Parametreler - sol ve sağ sütun
  const col2X = 250 * Scl;
  const params = [
    [`K  (Kat Sayısı)`, `= ${K}`, `A  (Kat Başı Daire)`, `= ${A}`],
    [`Rt (Tesisat Kablo Çapı)`, `= ${Rt} cm`, `Rtv (TV/Tel Kablo Çapı)`, `= ${Rtv} cm`],
    [`Rk (Kolon Hattı Kablo Çapı)`, `= ${Rk} cm`, `TİP`, `= ${TIP}`],
    [`Rk,top (En Büyük Kablo Çapı)`, `= ${Rktop} cm`, `Rk,max`, `= ${Rkmax} cm`],
  ];

  params.forEach(([label1, val1, label2, val2]) => {
    addText(result, gap, Y, label1, smallTextH);
    addText(result, 140 * Scl, Y, val1, smallTextH);
    addText(result, col2X, Y, label2, smallTextH);
    addText(result, col2X + 140 * Scl, Y, val2, smallTextH);
    Y -= rowHeight;
  });

  Y -= rowHeight * 0.5;
  result.push(Line(0, Y, leftWidth, Y, op_gray));
  Y -= rowHeight;

  // ──── Kablo Merdiveni Eni Hesabı ────
  addText(result, gap, Y, "KABLO MERDİVENİ TOPLAM ENİ (a)", 12 * Scl, op_blue);
  Y -= rowHeight * 1.2;

  addText(result, gap, Y, `TİP ${TIP} formülü:`, smallTextH);
  Y -= rowHeight;

  addText(result, gap, Y, formulText, smallTextH);
  Y -= rowHeight;

  addText(result, gap, Y, `a = ${a.toFixed(2)} cm`, textH, op_blue);
  Y -= rowHeight * 1.5;

  result.push(Line(0, Y, leftWidth, Y, op_gray));
  Y -= rowHeight;

  // ──── Dikme Yüksekliği ────
  addText(result, gap, Y, "DİKME YÜKSEKLİĞİ (h)", 12 * Scl, op_blue);
  Y -= rowHeight * 1.2;

  addText(result, gap, Y, `h >= Rk,max + 1 cm = ${Rkmax} + 1 = ${(Rkmax + 1).toFixed(1)} cm`, smallTextH);
  Y -= rowHeight;
  addText(result, gap, Y, `h min = 4 cm`, smallTextH);
  Y -= rowHeight;
  addText(result, gap, Y, `h = ${h.toFixed(1)} cm`, textH, op_blue);
  Y -= rowHeight * 1.5;

  result.push(Line(0, Y, leftWidth, Y, op_gray));
  Y -= rowHeight;

  // ──── Kablo Bacası Boyutları ────
  addText(result, gap, Y, "KABLO BACASI BOYUTLARI", 12 * Scl, op_blue);
  Y -= rowHeight * 1.2;

  addText(result, gap, Y, `Kablo Merdiveni Eni (a) = ${a.toFixed(2)} cm`, smallTextH);
  Y -= rowHeight;
  addText(result, gap, Y, `Kablo Bacası Eni (b) = a + 10 = ${(a + 10).toFixed(2)} cm  (min 50 cm)`, smallTextH);
  Y -= rowHeight;
  addText(result, gap, Y, `Kablo Bacası Eni (b) = ${kbEni.toFixed(2)} cm`, textH, op_blue);
  Y -= rowHeight;
  addText(result, gap, Y, `Kablo Bacası Derinliği = min ${depth} cm`, smallTextH);
  Y -= rowHeight * 1.5;

  result.push(Line(0, Y, leftWidth, Y, op_gray));
  Y -= rowHeight;

  // ──── Mukavemet Kontrolü ────
  addText(result, gap, Y, "MUKAVEMET KONTROLÜ", 12 * Scl, op_blue);
  Y -= rowHeight * 1.2;

  addText(result, gap, Y, `Dikme Profil Kalınlığı (d) = ${d} cm`, smallTextH);
  Y -= rowHeight;
  addText(result, gap, Y, `Mesnet Açıklığı (L) = ${L} cm`, smallTextH);
  Y -= rowHeight;
  addText(result, gap, Y, `E (Elastisite Modülü) = 2.1×10⁶ kg/cm²`, smallTextH);
  Y -= rowHeight;
  addText(result, gap, Y, `P (Kablo Yükü) = ${P.toFixed(2)} kg`, smallTextH);
  Y -= rowHeight * 1.2;

  addText(result, gap, Y, `Atalet Momenti:`, smallTextH);
  Y -= rowHeight;
  addText(result, gap, Y, `I = a×d×(h-d)³/12 = ${a.toFixed(2)}×${d}×(${h.toFixed(1)}-${d})³/12`, smallTextH);
  Y -= rowHeight;
  addText(result, gap, Y, `I = ${I_val.toFixed(4)} cm⁴`, textH, op_blue);
  Y -= rowHeight * 1.2;

  addText(result, gap, Y, `Sehim Kontrolü:`, smallTextH);
  Y -= rowHeight;
  addText(result, gap, Y, `f = P×L³/(48×E×I) = ${P.toFixed(2)}×${L}³/(48×${E}×${I_val.toFixed(4)})`, smallTextH);
  Y -= rowHeight;
  addText(result, gap, Y, `f = ${f_val.toFixed(4)} cm`, textH, op_blue);
  Y -= rowHeight;
  addText(result, gap, Y, `f izin = L/200 = ${L}/200 = ${f_limit.toFixed(2)} cm`, smallTextH);
  Y -= rowHeight * 1.2;

  // Sonuç kontrolü
  const isOk = f_val <= f_limit;
  const kontrolText = isOk
    ? `${f_val.toFixed(4)} cm < ${f_limit.toFixed(2)} cm  → UYGUNDUR`
    : `${f_val.toFixed(4)} cm > ${f_limit.toFixed(2)} cm  → UYGUN DEĞİLDİR`;
  addText(result, gap, Y, kontrolText, textH, isOk ? op_blue : op_gray);
  Y -= rowHeight * 1.5;

  result.push(Line(0, Y, leftWidth, Y, op_gray));
  Y -= rowHeight;

  // ──── Yangın Yönetmeliği Notu ────
  addText(result, gap, Y, "NOT: Binalarda Yangın Yönetmeliği gereği", smallTextH);
  Y -= rowHeight;
  addText(result, gap, Y, "kablo bacaları yangına dayanıklı malzeme ile", smallTextH);
  Y -= rowHeight;
  addText(result, gap, Y, "kapatılacak ve her katta yangın durdurucu", smallTextH);
  Y -= rowHeight;
  addText(result, gap, Y, "ile yalıtılacaktır.", smallTextH);
  Y -= rowHeight;

  const totalTextHeight = Math.abs(Y);

  // ──── Sağ taraf: DXF çizimi ────
  const drawing = KABLO_BACASI_DRAWING;
  const drawingEntities = [];

  // Çizim ölçeği: sağ tarafa sığdır
  const drawingTargetH = totalTextHeight * 0.85;
  const drawScale = drawingTargetH / drawing.height;
  const drawingW = drawing.width * drawScale;

  const drawOffsetX = leftWidth + 30 * Scl;
  const drawOffsetY = -totalTextHeight * 0.08; // biraz yukarı

  // Lines
  drawing.lines.forEach(l => {
    drawingEntities.push(Line(
      l.x1 * drawScale + drawOffsetX,
      -(l.y1 * drawScale) + drawOffsetY,
      l.x2 * drawScale + drawOffsetX,
      -(l.y2 * drawScale) + drawOffsetY,
      op_gray
    ));
  });

  // Polylines
  drawing.polylines.forEach(p => {
    const verts = p.vertices.map(v => ({
      x: v.x * drawScale + drawOffsetX,
      y: -(v.y * drawScale) + drawOffsetY,
      z: 0,
    }));
    if (p.closed && verts.length > 0) {
      verts.push({ ...verts[0] });
    }
    drawingEntities.push(PolyLine(0, 0, verts, op_gray));
  });

  // Circles
  drawing.circles.forEach(c => {
    drawingEntities.push(Circle(
      c.cx * drawScale + drawOffsetX,
      -(c.cy * drawScale) + drawOffsetY,
      c.r * drawScale,
      op_gray
    ));
  });

  // Arcs
  drawing.arcs.forEach(a => {
    drawingEntities.push(Arc(
      a.cx * drawScale + drawOffsetX,
      -(a.cy * drawScale) + drawOffsetY,
      a.r * drawScale,
      a.startAngle,
      a.endAngle,
      op_gray
    ));
  });

  // Ellipses (polyline yaklaşımı)
  if (drawing.ellipses) {
    drawing.ellipses.forEach(e => {
      const cx = e.cx * drawScale + drawOffsetX;
      const cy = -(e.cy * drawScale) + drawOffsetY;
      const rx = e.rx * drawScale;
      const ry = e.ry * drawScale;
      const sa = e.startAngle || 0;
      const ea = e.endAngle || Math.PI * 2;
      const segments = 32;
      const range = ea >= sa ? ea - sa : ea + Math.PI * 2 - sa;
      const verts = [];
      for (let i = 0; i <= segments; i++) {
        const t = sa + (range * i) / segments;
        verts.push({ x: cx + rx * Math.cos(t), y: cy - ry * Math.sin(t), z: 0 });
      }
      drawingEntities.push(PolyLine(0, 0, verts, op_gray));
    });
  }

  // Texts (çizim etiketleri)
  drawing.texts.forEach(t => {
    const tx = t.x * drawScale + drawOffsetX;
    const ty = -(t.y * drawScale) + drawOffsetY;
    const th = Math.max(t.height * drawScale, 4);
    addTextRotated(drawingEntities, tx, ty, t.text, t.rotation, th, op_gray);
  });

  result.push(...drawingEntities);

  // ──── Dış çerçeve ────
  const docWidth = drawOffsetX + drawingW + 30 * Scl;
  const docHeight = totalTextHeight + 20 * Scl;

  const borderVerts = [
    { x: 0, y: 10 * Scl, z: 0 },
    { x: 0, y: -(docHeight), z: 0 },
    { x: docWidth, y: -(docHeight), z: 0 },
    { x: docWidth, y: 10 * Scl, z: 0 },
    { x: 0, y: 10 * Scl, z: 0 },
  ];
  result.push(PolyLine(0, 0, borderVerts, op_gray));

  // Dikey ayırıcı (sol-sağ)
  result.push(Line(leftWidth, 10 * Scl, leftWidth, -(docHeight), op_gray));

  // Başlık alt çizgisi
  const titleY = rowHeight * 1.5 + 5 * Scl;
  result.push(Line(0, -titleY + rowHeight * 1.5 + 10 * Scl, leftWidth, -titleY + rowHeight * 1.5 + 10 * Scl, op_gray));

  return { result, width: docWidth, height: docHeight };
};
